import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  Textarea,
} from '@cartcloser/ui';
import {
  createCategory,
  importFeed,
  ingestDocument,
  listCategories,
  listProductDocuments,
  listProducts,
  type Product,
} from '../lib/api.js';

export const Route = createFileRoute('/admin/$slug/catalog')({
  component: CatalogPage,
});

const SAMPLE_CSV = `sku,title,brand,price,in_stock,category,place_settings,noise_db,width_mm,height_mm,depth_mm,energy_rating,wash_programs,water_use_l,third_rate,warranty_years
DW-GRANDE,MaxWash Grande,Volt,999.00,yes,Dishwashers,15,44,600,845,600,4.5,8,13.5,yes,5
DW-MID,MidWash Standard,Volt,799.00,yes,Dishwashers,12,45,600,845,600,4,6,14,no,2
DW-COMPACT,SlimWash Compact,Volt,599.00,yes,Dishwashers,8,46,450,845,600,3.5,5,11,no,2
DW-QUIET,QuietCore 39,Volt,1099.00,yes,Dishwashers,12,39,600,845,575,5,9,11.5,yes,5
DW-BUDGET,ValueWash,Volt,499.00,no,Dishwashers,12,53,600,845,600,3,4,16,no,1
MW-3PK,MeshPro 6 (3-pack),NetGear,349.00,yes,Mesh wi-fi,,,,,,,,,,2`;

function CatalogPage() {
  const { slug } = Route.useParams();
  const queryClient = useQueryClient();
  const categories = useQuery({ queryKey: ['categories', slug], queryFn: () => listCategories(slug) });
  const products = useQuery({ queryKey: ['products', slug], queryFn: () => listProducts(slug) });

  const [feed, setFeed] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['products', slug] }),
      queryClient.invalidateQueries({ queryKey: ['categories', slug] }),
    ]);
  };

  const importMut = useMutation({
    mutationFn: () => importFeed(slug, feed),
    onSuccess: async (r) => {
      setMsg(`Imported ${r.imported} products.`);
      setErr(null);
      setFeed('');
      await refresh();
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Import failed'),
  });

  const createCat = useMutation({
    mutationFn: () => createCategory(slug, categoryName),
    onSuccess: async () => {
      setCategoryName('');
      await refresh();
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'Failed'),
  });

  const catName = (id: string | null) =>
    categories.data?.find((c) => c.id === id)?.name ?? '—';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Catalogue</h1>
        <p className="text-sm text-muted-foreground">
          Import products from a CSV/JSON feed. Categories are created automatically from the feed.
        </p>
      </div>

      {msg ? (
        <Alert variant="success">
          <AlertDescription>{msg}</AlertDescription>
        </Alert>
      ) : null}
      {err ? (
        <Alert variant="destructive">
          <AlertDescription>{err}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Import feed</CardTitle>
          <CardDescription>
            CSV (with a header row) or JSON array. Columns beyond sku/title/brand/price/in_stock/category
            become product attributes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            rows={8}
            value={feed}
            onChange={(e) => setFeed(e.target.value)}
            placeholder="sku,title,brand,price,in_stock,category…"
            className="font-mono text-xs"
          />
          <div className="flex gap-2">
            <Button loading={importMut.isPending} onClick={() => importMut.mutate()} disabled={!feed.trim()}>
              Import
            </Button>
            <Button variant="ghost" onClick={() => setFeed(SAMPLE_CSV)}>
              Load sample
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Categories</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                createCat.mutate();
              }}
            >
              <Input
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="New category"
              />
              <Button type="submit" size="sm" loading={createCat.isPending}>
                Add
              </Button>
            </form>
            <ul className="space-y-1 text-sm">
              {categories.data?.map((c) => (
                <li key={c.id} className="flex justify-between">
                  <span>{c.name}</span>
                  <span className="text-muted-foreground">{c.slug}</span>
                </li>
              ))}
              {categories.data?.length === 0 ? (
                <li className="text-muted-foreground">No categories yet.</li>
              ) : null}
            </ul>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Products ({products.data?.length ?? 0})</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-2 text-xs text-muted-foreground">
              Click a row to see exactly what’s stored against the SKU — the specs the agent reasons
              over and any ingested knowledge.
            </p>
            <div className="divide-y divide-border/60">
              {products.data?.map((p) => (
                <ProductRow key={p.id} slug={slug} product={p} categoryName={catName(p.categoryId)} />
              ))}
              {products.data?.length === 0 ? (
                <p className="py-4 text-sm text-muted-foreground">
                  No products yet. Import a feed above.
                </p>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>

      <DocumentsPanel slug={slug} products={products.data ?? []} />
    </div>
  );
}

/** Tolerate a malformed attributesJson row rather than crashing the render. */
function parseAttributes(json: string | null): Record<string, unknown> {
  if (!json) return {};
  try {
    const parsed = JSON.parse(json) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function ProductRow({
  slug,
  product,
  categoryName,
}: {
  slug: string;
  product: Product;
  categoryName: string;
}) {
  const [open, setOpen] = useState(false);
  const attributes = parseAttributes(product.attributesJson);
  const attrCount = Object.keys(attributes).length;

  const docs = useQuery({
    queryKey: ['documents', slug, product.id],
    queryFn: () => listProductDocuments(slug, product.id),
    enabled: open,
  });

  return (
    <div className="py-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left text-sm"
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="text-muted-foreground">{open ? '▾' : '▸'}</span>
          <span className="font-mono text-xs text-muted-foreground">{product.sku}</span>
          <span className="truncate">{product.title}</span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <Badge variant={attrCount > 0 ? 'success' : 'warning'}>
            {attrCount} spec{attrCount === 1 ? '' : 's'}
          </Badge>
          <span className="text-muted-foreground">
            {product.priceCents != null ? `$${(product.priceCents / 100).toFixed(2)}` : '—'}
          </span>
          <Badge variant={product.inStock ? 'success' : 'secondary'}>
            {product.inStock ? 'In stock' : 'Out'}
          </Badge>
        </span>
      </button>

      {open ? (
        <div className="mt-3 grid gap-4 rounded-lg bg-muted/40 p-3 sm:grid-cols-2">
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Stored specs (attributesJson)
            </p>
            {attrCount > 0 ? (
              <table className="w-full text-xs">
                <tbody>
                  {Object.entries(attributes).map(([k, v]) => (
                    <tr key={k} className="border-b border-border/40">
                      <td className="py-1 pr-3 font-mono text-muted-foreground">{k}</td>
                      <td className="py-1">{String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-xs italic text-destructive">
                No specs stored — this SKU can’t support nuanced recommendations. Add spec columns to
                the feed or ingest a spec sheet.
              </p>
            )}
            <p className="mt-2 text-[11px] text-muted-foreground">
              Category: {categoryName}
              {product.mappingGroupId ? ' · part of a cross-SKU group' : ''}
            </p>
          </div>
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Ingested knowledge (Mode B)
            </p>
            {docs.isLoading ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : docs.data && docs.data.knowledge.length > 0 ? (
              <ul className="space-y-1">
                {docs.data.knowledge.map((k) => (
                  <li key={k.id} className="text-xs">
                    <Badge variant="secondary" className="mr-1">
                      {k.topic}
                    </Badge>
                    {k.heading ? <span className="font-medium">{k.heading}: </span> : null}
                    <span className="text-muted-foreground">{k.content}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs italic text-muted-foreground">
                No document knowledge yet. Ingest a manual below.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DocumentsPanel({ slug, products }: { slug: string; products: Product[] }) {
  const queryClient = useQueryClient();
  const [productId, setProductId] = useState('');
  const [text, setText] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const docs = useQuery({
    queryKey: ['documents', slug, productId],
    queryFn: () => listProductDocuments(slug, productId),
    enabled: productId !== '',
  });

  const ingest = useMutation({
    mutationFn: () => ingestDocument(slug, { productId, text }),
    onSuccess: async (r) => {
      setResult(`Digested into ${r.knowledgeCount} knowledge topics + ${Object.keys(r.attributes).length} attributes.`);
      setError(null);
      setText('');
      await queryClient.invalidateQueries({ queryKey: ['documents', slug, productId] });
      await queryClient.invalidateQueries({ queryKey: ['products', slug] });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Ingestion failed'),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Documents & knowledge</CardTitle>
        <CardDescription>
          Paste a product manual or spec sheet. We digest it once at upload into structured specs +
          knowledge topics; chat answers then come from fast lookups (no re-parsing).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <FormField label="Product">
          {({ id }) => (
            <select
              id={id}
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="">Select a product…</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.sku} — {p.title}
                </option>
              ))}
            </select>
          )}
        </FormField>
        {productId ? (
          <>
            <Textarea
              rows={6}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste the manual / spec sheet text here…"
            />
            <Button
              loading={ingest.isPending}
              disabled={!text.trim()}
              onClick={() => ingest.mutate()}
            >
              Digest document
            </Button>
            {result ? (
              <Alert variant="success">
                <AlertDescription>{result}</AlertDescription>
              </Alert>
            ) : null}
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            {docs.data && docs.data.knowledge.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium">Knowledge topics</p>
                {docs.data.knowledge.map((k) => (
                  <div key={k.id} className="rounded-md border border-border p-3 text-sm">
                    <Badge variant="secondary" className="mb-1">
                      {k.topic}
                    </Badge>
                    {k.heading ? <p className="font-medium">{k.heading}</p> : null}
                    <p className="text-muted-foreground">{k.content}</p>
                  </div>
                ))}
              </div>
            ) : null}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
